import React, { createContext, useState, useCallback, useContext, useEffect, useRef } from 'react';
import ConfirmModal from '../components/ConfirmModal/ConfirmModal';
import PromptModal from '../components/ConfirmModal/PromptModal';
import { TOAST_EVENT, CONFIRM_EVENT, PROMPT_EVENT } from '../utils/notify';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const AUTO_DISMISS_MS = 9000;
// Must match the toast-out animation in Toast.css.
const LEAVE_MS = 180;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // A toast fades out before it is removed, instead of vanishing.
  const dismiss = useCallback((id) => {
    setToasts(prev => prev.map(t => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, LEAVE_MS);
  }, []);

  const toast = useCallback((message, type = 'error') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
  }, [dismiss]);

  // notify() from outside React (utils/notify.js).
  useEffect(() => {
    const onToast = (e) => toast(e.detail?.message, e.detail?.type);
    window.addEventListener(TOAST_EVENT, onToast);
    return () => window.removeEventListener(TOAST_EVENT, onToast);
  }, [toast]);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <ToastList toasts={toasts} onDismiss={dismiss} />
      <ConfirmHost />
      <PromptHost />
    </ToastContext.Provider>
  );
};

/** Answers confirmAsync() (utils/notify.js) with the suite's ConfirmModal. */
function ConfirmHost() {
  const [request, setRequest] = useState(null);
  const pending = useRef(null);

  useEffect(() => {
    const onConfirm = (e) => {
      const detail = e.detail;
      if (!detail) return;
      detail.handled = true;
      // A second request while one is open answers the first "no", as a new native dialog would.
      if (pending.current) pending.current(false);
      pending.current = detail.resolve;
      setRequest(detail);
    };
    window.addEventListener(CONFIRM_EVENT, onConfirm);
    return () => window.removeEventListener(CONFIRM_EVENT, onConfirm);
  }, []);

  const settle = (answer) => {
    const resolve = pending.current;
    pending.current = null;
    setRequest(null);
    if (resolve) resolve(answer);
  };

  return (
    <ConfirmModal
      open={!!request}
      hideNote
      title={request?.title || 'Are you sure?'}
      message={request?.message || ''}
      confirmLabel={request?.confirmLabel || 'Confirm'}
      closeLabel={request?.closeLabel || 'Cancel'}
      onConfirm={() => settle(true)}
      onClose={() => settle(false)}
    />
  );
}

/** Answers promptAsync() (utils/notify.js) with the suite's PromptModal. */
function PromptHost() {
  const [request, setRequest] = useState(null);
  const pending = useRef(null);

  useEffect(() => {
    const onPrompt = (e) => {
      const detail = e.detail;
      if (!detail) return;
      detail.handled = true;
      // A second request while one is open cancels the first, as a new native prompt would.
      if (pending.current) pending.current(null);
      pending.current = detail.resolve;
      setRequest(detail);
    };
    window.addEventListener(PROMPT_EVENT, onPrompt);
    return () => window.removeEventListener(PROMPT_EVENT, onPrompt);
  }, []);

  const settle = (answer) => {
    const resolve = pending.current;
    pending.current = null;
    setRequest(null);
    if (resolve) resolve(answer);
  };

  return (
    <PromptModal
      open={!!request}
      title={request?.title}
      message={request?.message}
      label={request?.label}
      placeholder={request?.placeholder}
      defaultValue={request?.defaultValue || ''}
      confirmLabel={request?.confirmLabel}
      closeLabel={request?.closeLabel}
      onConfirm={(value) => settle(value)}
      onClose={() => settle(null)}
    />
  );
}

function ToastList({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div className="toast-container" role="region" aria-label="Notifications">
      {toasts.map(({ id, message, type, leaving }) => (
        <div
          key={id}
          className={`toast toast-${type}${leaving ? ' is-leaving' : ''}`}
          role="alert"
        >
          <span className="toast-message">{message}</span>
          <button
            type="button"
            className="toast-dismiss"
            onClick={() => onDismiss(id)}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}

export default ToastContext;
